import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  ArrowPathIcon,
  BanknotesIcon,
  CameraIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { IKanbanLead, LeadPipelineStage, PaymentStatus } from "../../models/Lead";
import useLeadKanban from "../../data/useLeadKanban";
import openInNewTab from "../../utils/openInNewTab";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import usePricingAccess from "../../hooks/usePricingAccess";
import LoadingWheel from "../shared/LoadingWheel";

// Columns shown on the board, left to right.
const KANBAN_COLUMN_STAGES: { stage: LeadPipelineStage; labelKey: string }[] = [
  { stage: LeadPipelineStage.AwaitingCollection, labelKey: "Awaiting Collection" },
  { stage: LeadPipelineStage.Booked, labelKey: "Booked" },
  { stage: LeadPipelineStage.Collected, labelKey: "Collected / Dropped Off" },
  { stage: LeadPipelineStage.AwaitingPayment, labelKey: "Awaiting Payment" },
  { stage: LeadPipelineStage.AwaitingCod, labelKey: "Awaiting COD" },
  { stage: LeadPipelineStage.Complete, labelKey: "Complete" },
];

// Whole-number days since the lead was created, for an at-a-glance age badge.
function ageInDays(created: string): number | null {
  if (!created) return null;
  const days = DateTime.now().diff(DateTime.fromISO(created, { zone: "utc" }), "days").days;
  return Math.max(0, Math.floor(days));
}

const BADGE_CLASSES = "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium";

function Badge({
  tone,
  icon,
  children,
  title,
}: {
  tone: "neutral" | "amber" | "green";
  icon?: ReactNode;
  children: ReactNode;
  title?: string;
}) {
  const tones = {
    neutral: "bg-gray-50 border-gray-300 text-gray-500",
    amber: "bg-amber-50 border-amber-300 text-amber-700",
    green: "bg-green-50 border-green-300 text-green-700",
  };

  return (
    <span className={`${BADGE_CLASSES} ${tones[tone]}`} title={title}>
      {icon}
      {children}
    </span>
  );
}

function KanbanCard({ item }: { item: IKanbanLead }) {
  const { t } = useTranslation("leads");
  const { canViewPrices } = usePricingAccess();
  const { lead, todos, driverName, checkInCompletedCount, checkInTotalSteps } = item;
  const vehicle = lead.vehicle;
  const age = ageInDays(lead.created);

  const isPaid = lead.paymentStatus === PaymentStatus.Sent || lead.paymentStatus === PaymentStatus.Approved;
  const isPaymentRaised = lead.paymentStatus === PaymentStatus.Raised || lead.paymentStatus === PaymentStatus.Failed;
  const codOutstanding = !!vehicle?.codRequired && !vehicle?.codConfirmedOn;
  const checkedIn = checkInTotalSteps > 0 && checkInCompletedCount >= checkInTotalSteps;
  const notes = [lead.officeNotes, lead.driverNotes, lead.yardNotes].filter(Boolean).join("\n");

  return (
    <div
      onClick={() => openInNewTab(`leads/${lead.id}`)}
      className="bg-white rounded-lg border border-gray-300 p-3 shadow-sm hover:shadow-md hover:border-primary-400 cursor-pointer transition"
    >
      <div className="flex gap-x-2">
        <div className="flex-shrink-0 flex items-center justify-center w-16 h-12">
          {vehicle?.vehicleImageUrl ? (
            <img className="w-16 h-12 object-contain" src={vehicle.vehicleImageUrl} alt={vehicle.vrm} />
          ) : (
            <CameraIcon className="w-8 h-8 text-gray-400" />
          )}
        </div>

        <div className="flex flex-col min-w-0 flex-grow">
          <span className="font-semibold text-sm truncate">{vehicle?.vrm ?? "-"}</span>
          <span className="text-xs text-gray-600 truncate">
            {[vehicle?.combinedMake, vehicle?.modelRangeDescription].filter(Boolean).join(" ") || "-"}
          </span>
          {canViewPrices && lead.price ? (
            <span className="text-xs text-gray-800">{PriceFormat(lead.price)}</span>
          ) : null}
        </div>

        {notes && (
          <span className="flex-shrink-0" title={notes}>
            <DocumentTextIcon className="h-4 w-4 text-gray-400" />
          </span>
        )}
      </div>

      <div className="mt-1.5 flex items-baseline justify-between gap-2 text-[11px] text-gray-500">
        <span className="truncate">{lead.contact?.fullname ?? "-"}</span>
        <span className="flex-shrink-0 font-medium">{vehicle?.postcode ?? ""}</span>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        <Badge
          tone={driverName ? "green" : "neutral"}
          icon={<UserIcon className="h-3 w-3" />}
          title={t("Assigned driver")}
        >
          {driverName ?? t("Unassigned")}
        </Badge>

        {checkInTotalSteps > 0 && (
          <Badge
            tone={checkedIn ? "green" : checkInCompletedCount > 0 ? "amber" : "neutral"}
            icon={<ClipboardDocumentCheckIcon className="h-3 w-3" />}
            title={t("Check-in progress")}
          >
            {checkedIn ? t("Checked in") : `${checkInCompletedCount}/${checkInTotalSteps}`}
          </Badge>
        )}

        <Badge
          tone={isPaid ? "green" : isPaymentRaised ? "amber" : "neutral"}
          icon={<BanknotesIcon className="h-3 w-3" />}
          title={t("Payment status")}
        >
          {isPaid ? t("Paid") : isPaymentRaised ? t("Raised") : t("Raise")}
        </Badge>

        {codOutstanding && (
          <Badge tone="amber" icon={<DocumentTextIcon className="h-3 w-3" />} title={t("Certificate of destruction outstanding")}>
            {t("COD")}
          </Badge>
        )}
      </div>

      {todos?.length > 0 && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-400">{t("To do")}</span>
          <ul className="mt-1 flex flex-col gap-1">
            {todos.map((todo, i) => (
              <li
                key={i}
                className={`flex items-center gap-1.5 text-[11px] ${todo.complete ? "text-gray-400" : "text-gray-700 font-medium"}`}
              >
                {todo.complete ? (
                  <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                ) : (
                  <span className="h-3 w-3 flex-shrink-0 rounded-full border border-gray-400" />
                )}
                <span className={todo.complete ? "line-through" : ""}>{t(todo.label)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
        <span>
          {lead.completedOn
            ? t("Completed: {{date}}", { date: formatDateTime(lead.completedOn, DateTime.DATE_SHORT) })
            : vehicle?.collectedOn
              ? t("On site: {{date}}", { date: formatDateTime(vehicle.collectedOn, DateTime.DATE_SHORT) })
              : vehicle?.scheduledCollectionOn
                ? t("Booked: {{date}}", { date: formatDateTime(vehicle.scheduledCollectionOn, DateTime.DATE_SHORT) })
                : t("Created: {{date}}", { date: formatDateTime(lead.created, DateTime.DATE_SHORT) })}
        </span>
        {age !== null && (
          <span className="rounded-full bg-gray-100 border border-gray-300 px-2 py-0.5 font-medium text-gray-700">
            {age}d
          </span>
        )}
      </div>
    </div>
  );
}

export default function LeadKanbanBoard({
  max = 25,
  fillHeight = false,
}: {
  max?: number;
  // When true, the board stretches to fill its parent's height and each column
  // scrolls independently. When false (e.g. inside a modal) columns cap at 65vh.
  fillHeight?: boolean;
}) {
  const { t } = useTranslation("leads");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");

  // The search hits the API (so it can reach leads beyond the per-column cap), so it's
  // debounced rather than fired on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { kanban } = useLeadKanban(max, query);

  const board = kanban.data?.data;

  // Show the in-box spinner both while the debounce is still pending (typed but not yet
  // searched) and while the request is in flight — but not on the very first board load,
  // which has its own full-height loading wheel below.
  const debouncePending = searchInput.trim() !== query;
  const searching = !kanban.isLoading && (debouncePending || kanban.isFetching);

  // Full pipeline count per stage (the whole funnel, not just the capped cards shown here).
  const fullCounts = useMemo(() => {
    const counts = new Map<LeadPipelineStage, number>();
    board?.counts?.forEach((pc) => counts.set(pc.stage, pc.count));
    return counts;
  }, [board]);

  // Bucket the (already oldest-first) cards into their stage columns.
  const columns = useMemo(() => {
    const grouped = new Map<LeadPipelineStage, IKanbanLead[]>();
    KANBAN_COLUMN_STAGES.forEach((c) => grouped.set(c.stage, []));
    board?.cards?.forEach((item) => {
      grouped.get(item.stage)?.push(item);
    });
    return grouped;
  }, [board]);

  return (
    <div className={fillHeight ? "flex flex-col h-full min-h-0" : "py-4"}>
      <div className="flex flex-wrap items-center justify-between gap-2 pb-3 flex-shrink-0">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("Search VRM, name or postcode")}
            className="h-9 w-72 rounded-md border-gray-300 pl-8 pr-8 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
          {searching && (
            <span
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
              aria-label={t("Searching")}
            >
              <LoadingWheel className="w-4 h-4" wrapperClassName="" />
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => kanban.refetch()}
          disabled={kanban.isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 h-9 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${kanban.isFetching ? "animate-spin" : ""}`} />
          {t("Refresh")}
        </button>
      </div>

      {kanban.isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingWheel />
        </div>
      ) : kanban.isError ? (
        <div className="py-16 text-center text-red-600 text-sm">
          {t("Couldn't load the collections board. {{message}}", {
            message: (kanban.error as Error)?.message ?? "",
          })}
        </div>
      ) : (
        <div className={`flex gap-3 overflow-x-auto pb-2${fillHeight ? " flex-1 min-h-0" : ""}`}>
          {KANBAN_COLUMN_STAGES.map((col) => {
            const cards = columns.get(col.stage) ?? [];
            const fullCount = fullCounts.get(col.stage);
            const truncated = fullCount !== undefined && fullCount > cards.length;
            const isCompleteColumn = col.stage === LeadPipelineStage.Complete;
            return (
              <div
                key={col.stage}
                className={`flex-shrink-0 w-72 bg-gray-50 rounded-lg border border-gray-200${fillHeight ? " flex flex-col min-h-0" : ""}`}
              >
                <div className="sticky top-0 flex items-center justify-between gap-2 px-3 py-2 bg-gray-100 rounded-t-lg border-b border-gray-200">
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{t(col.labelKey)}</span>
                    {isCompleteColumn ? (
                      <span className="text-[10px] text-gray-400 truncate">
                        {t("last {{count}} days", { count: board?.completeWindowDays ?? 30 })}
                        {truncated && ` · ${t("showing newest {{count}}", { count: cards.length })}`}
                      </span>
                    ) : truncated ? (
                      <span className="text-[10px] text-gray-400">
                        {t("showing oldest {{count}}", { count: cards.length })}
                      </span>
                    ) : null}
                  </div>
                  <span
                    className="inline-flex items-center justify-center text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-full h-6 min-w-[1.5rem] px-2 leading-none"
                    title={t("Total collections in this stage")}
                  >
                    {fullCount ?? cards.length}
                  </span>
                </div>
                <div className={`p-2 flex flex-col gap-2 min-h-[4rem] overflow-y-auto${fillHeight ? " flex-1" : " max-h-[65vh]"}`}>
                  {cards.length === 0 ? (
                    <div className="text-center text-xs text-gray-400 py-4">{t("Nothing here")}</div>
                  ) : (
                    <>
                      {cards.map((c) => <KanbanCard key={c.lead.id} item={c} />)}
                      {truncated && (
                        <div className="text-center text-xs font-medium text-gray-500 py-2">
                          {t("+ {{count}} more...", { count: fullCount! - cards.length })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
